import React, { useEffect, useState, useMemo } from 'react';
import validator from 'validator';
import { Typography } from '../typography';
import { Button } from '../button';
import { Input, InputPrice } from '../input';
import { CheckboxLabel } from '../checkbox';
import {
  ModalBase,
  ModalWrapper,
  ModalFooterButtons,
  ModalBodyCustomColumns,
} from './ModalComponents';
import { enqueueSnackbar } from '../snackbar';
import {
  useIsSmallScreen,
  useMultipleSalesFeeQueries,
  useSellContainerMutation,
} from '../../hooks';
import { calculatePriceWithFee, formatContainerType } from '../../utils';
import { ContainerType, FeeType } from '../../types';
import { INPUT_ERRORS, MESSAGES } from '../../const';
import { i18n } from '../../i18n';

interface ContainerSellModalType {
  data: {
    id: string;
    number: string;
    type: ContainerType;
    yardId: string;
  }[];
  isSubmitClicked: boolean;
  submitClick: () => void;
  open: () => void;
}

export const containerSellModal: ContainerSellModalType = {
  isSubmitClicked: false,
  submitClick: () => null,
  open: () => null,
  data: [],
};

export const ContainerSellModal = () => {
  const { mutate } = useSellContainerMutation();
  const salesFeeQueries = useMultipleSalesFeeQueries(containerSellModal.data);
  const allFees = salesFeeQueries.map((query) => query.data);
  const [open, setOpen] = useState(false);
  const [isSamePrice, setIsSamePrice] = useState(false);
  const [price, setPrice] = useState([0]);
  const [allowCounterOffers, setAllowCounterOffers] = useState(false);
  const [description, setDescription] = useState('');
  const total = useMemo(() => {
    return price.reduce(
      (acc, val, i) =>
        acc +
        calculatePriceWithFee(val, {
          type: allFees[i]?.feeType,
          value: allFees[i] ? allFees[i][containerSellModal.data[i]?.type] : 0,
        }),
      0,
    );
  }, [price, allFees, containerSellModal.data]);

  const isSmallScreen = useIsSmallScreen();
  const [, setUpdate] = useState<any>();

  const validators = {
    description: description
      ? !validator.isLength(description, { max: 500 })
        ? INPUT_ERRORS.NOT_VALID.SALE_DESCRIPTION_LENGTH
        : ''
      : '',
  };

  useEffect(() => {
    containerSellModal.open = () => {
      containerSellModal.isSubmitClicked = false;
      setPrice(Array(containerSellModal.data.length).fill(0));
      setAllowCounterOffers(false);
      setDescription('');
      setOpen(true);
    };
  }, []);

  const handleOnClose = () => {
    containerSellModal.data = [];
    setOpen(!open);
  };

  const handlePriceChange = (e: any, i: number) => {
    if (isSamePrice) {
      const newPrice = Array(price.length).fill(Number(e.target.value));
      setPrice(newPrice);

      return;
    }

    const newPrice = [...price];
    newPrice[i] = Number(e.target.value);
    setPrice(newPrice);
  };

  const handleSubmitClick = async () => {
    containerSellModal.isSubmitClicked = true;
    setUpdate(Math.random());

    if ([...Object.values(validators).map((val) => !val)].some((val) => !val)) {
      return;
    }

    containerSellModal.isSubmitClicked = false;

    mutate(
      containerSellModal.data.map((container, i) => ({
        id: container.id,
        ownerSetPrice: price[i],
        allowCounterOffers,
        description,
      })),
      {
        onSuccess: () => {
          setOpen(false);
          containerSellModal.submitClick();
        },
        onError: () => {
          enqueueSnackbar(MESSAGES.CONTAINERS.ERROR.NOT_SELL, 'error');
        },
      },
    );
  };

  return (
    <ModalBase
      id={'container-sell-modal'}
      paperSize={isSmallScreen ? 'mobile' : 'very-big'}
      open={open}
      onClose={handleOnClose}
    >
      <ModalWrapper>
        <Typography variant={'textL'} weight={'bold'}>
          {!isSmallScreen && i18n.t('pages.modal.containerSellTitle', { total })}
          {isSmallScreen && i18n.t('pages.modal.containerSellTitleShort', { total })}
        </Typography>
        {!isSmallScreen && (
          <>
            <ModalBodyCustomColumns columns={'1fr 4fr 0fr 0fr 0fr'} withInputHeight>
              {isSamePrice && (
                <InputPrice
                  data-size={'medium'}
                  value={price[0]}
                  onChange={(e) => handlePriceChange(e, 0)}
                />
              )}
              <CheckboxLabel
                label={i18n.t('label.samePriceForAll')}
                checked={isSamePrice}
                onChange={(e) => setIsSamePrice(e.target.checked)}
              />
            </ModalBodyCustomColumns>
            <ModalBodyCustomColumns columns={'1fr 1fr 1fr 1fr 1fr'} isSticky>
              <Typography variant={'textS'}>{i18n.t('label.containerNumber')}</Typography>
              <Typography variant={'textS'}>{i18n.t('label.containerType')}</Typography>
              <Typography variant={'textS'}>{i18n.t('label.price')}*</Typography>
              <Typography variant={'textS'}>{i18n.t('label.fee')}</Typography>
              <Typography variant={'textS'}>{i18n.t('label.finalPrice')}</Typography>
            </ModalBodyCustomColumns>
          </>
        )}
        {containerSellModal.data.map((container, i) => (
          <React.Fragment key={i}>
            <ModalBodyCustomColumns columns={isSmallScreen ? '1fr' : '1fr 1fr 1fr 1fr 1fr'}>
              <Input
                label={isSmallScreen ? i18n.t('label.containerNumber') : ''}
                data-size={'medium'}
                defaultValue={container.number}
                readOnly
              />
              <Input
                label={isSmallScreen ? i18n.t('label.containerType') : ''}
                data-size={'medium'}
                defaultValue={formatContainerType(container.type)}
                readOnly
              />
              <InputPrice
                label={isSmallScreen ? i18n.t('label.price') : ''}
                data-size={'medium'}
                value={price[i]}
                onChange={(e) => handlePriceChange(e, i)}
              />
              <InputPrice
                label={isSmallScreen ? i18n.t('label.fee') : ''}
                data-size={'medium'}
                value={allFees[i] ? allFees[i][container.type] : 0}
                readOnly
                isPercent={allFees[i].feeType === FeeType.PERCENTAGE}
              />
              <InputPrice
                label={isSmallScreen ? i18n.t('label.finalPrice') : ''}
                data-size={'medium'}
                value={calculatePriceWithFee(price[i], {
                  type: allFees[i].feeType,
                  value: allFees[i][container.type],
                })}
                readOnly
              />
            </ModalBodyCustomColumns>
            {/* <Input
          label={i18n.t('label.description')}
          placeholder={''}
          data-size={'medium'}
          rows={4}
          multiline
          defaultValue={description}
          onChange={(e) => setDescription(e.target.value)}
          error={!!validators.description}
          helperText={validators.description}
        /> */}
            {/* <CheckboxLabel
          label="Allow Counter Offers"
          checked={allowCounterOffers}
          onChange={(e) => setAllowCounterOffers(e.target.checked)}
        /> */}
          </React.Fragment>
        ))}
        <ModalFooterButtons>
          <Button size={'medium'} data-color={'white'} onClick={handleOnClose}>
            {i18n.t('button.cancel')}
          </Button>
          <Button
            size={'medium'}
            data-color={'primary'}
            onClick={handleSubmitClick}
            disabled={!!validators.description}
          >
            {i18n.t('button.submit')}
          </Button>
        </ModalFooterButtons>
      </ModalWrapper>
    </ModalBase>
  );
};
